import React, { useEffect, useState } from "react";
import "./ProductRegister.scss";
import InfoStep from "./InfoStep/InfoStep";
import { Button, Modal } from "antd";
import PricingStep from "./PricingStep/PricingStep";
import ImageStep from "./ImageStep/ImageStep";
import { useUser } from "contexts/UserContext";
import { IProductForm } from "@utils/Interfaces";
import Helpers from "@utils/Helpers";
import { ProductApi } from "services/ProductApi";
import { useNavigate } from "react-router-dom";
import { errorLog } from "logging";
import { useUnit } from "contexts/UnitContext";
import HayCalculations from "@utils/HayCalculations";
import { ExclamationCircleOutlined } from "@ant-design/icons";

interface IProductRegisterProps {
  data?: IProductForm;
  context?: string;
}

const ProductRegister: React.FC<IProductRegisterProps> = ({
  data,
  context,
}) => {
  const { units, convertCurrency, convertWeight } = useUnit();
  const [currentStep, setCurrentStep] = useState(1);
  const { userProfile, setIsNewProductState } = useUser();
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSmartEstimate, setIsSmartEstimate] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [isTriggerModal, setIsTriggerModal] = useState(false);
  const [hayTypeClassify, setHayTypeClassify] = useState("");
  const [isEstimate, setIsEstimate] = useState(true);
  const [isTypingTotalWeight, setIsTypingTotalWeight] = useState(false);
  const [isSmartEstimating, setIsSmartEstimating] = useState(false);
  const [sizeSelect, setSizeSelect] = useState("");
  const [selectedWeightUnit, setSelectedWeightUnit] = useState({
    weightPerBale: "t",
    totalWeight: "t",
  });
  const [dimensions, setDimensions] = useState<any>({
    width: 0,
    length: 0,
    height: 0,
  });
  const navigate = useNavigate();

  const [productForm, setProductForm] = useState<IProductForm>(
    data || {
      images: [],
      address: {
        street: "",
        city: "",
        province: "",
        zip_code: "",
        lat: 0,
        lon: 0,
      },
      product_category_id: 1,
      hay_type: "",
      hay_availability: "",
      shape: "ssquare",
      size: "",
      rain: "No",
      moisture: "",
      hay_compression: "medium",
      hay_density: "",
      weight_per_bale: "",
      total_weight: "",
      hay_cut: "1st cut",
      type_of_binding: "Twine",
      crop: "New",
      storage: "Covered",
      farm_id: Number(userProfile?.farm_id) || null,
      selling_method: "fixed price",
      price_per_tons: 0,
      price_per_bale: 0,
      current_price: 0,
      hay_unit: "Bale",
      shipping_options_details: {},
      shipping_cost: 0,
      payment_info_ids: [],
      return_within: "7",
      return_shipping_by: "buyer",
      crude_protein: 0,
    }
  );

  // TRIGGER FILL OUT FARM PROFILE
  useEffect(() => {
    if (
      !userProfile?.farm_info.farm_name ||
      !userProfile?.farm_info.address_id
    ) {
      setIsTriggerModal(true);
    }
  }, []);

  // HANDLE FETCH METRICS
  useEffect(() => {
    const fetchMetric = async () => {
      try {
        const response = await ProductApi.GetMetric(
          productForm.hay_type,
          productForm.shape
        );
        if (response.status === 200) {
          setMetrics(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch metrics");
        errorLog("ProductRegister.tsx - fetchMetric", error);
      }
    };
    if (productForm.hay_type !== "" && productForm.shape !== "") {
      fetchMetric();
    }
  }, [productForm.hay_type, productForm.shape]);

  // HANDLE NEXT STEP
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (productForm.images.length === 0) {
        Helpers.notification.warning("At least 1 image for your product");
        return;
      }
      if (isSmartEstimate) {
        estimateHay();
      }
    }
    if (currentStep === 2) {
      if (
        productForm.hay_type === "" ||
        productForm.hay_availability === "" ||
        productForm.shape === "" ||
        productForm.size === "" ||
        productForm.rain === "" ||
        productForm.moisture === "" ||
        productForm.hay_density === "" ||
        productForm.weight_per_bale === "" ||
        productForm.total_weight === "" ||
        productForm.hay_cut === "" ||
        productForm.type_of_binding === "" ||
        productForm.crop === "" ||
        productForm.storage === "" ||
        productForm.address.province === "" ||
        productForm.address.zip_code === "" ||
        productForm.address.street === ""
      ) {
        Helpers.notification.warning("Please fill out the required fields");
        return;
      }
      if (Number(productForm.hay_availability) % 1 !== 0) {
        Helpers.notification.warning("Hay availability must be a whole number");
        return;
      }
      if (Number(productForm.hay_availability) <= 0) {
        Helpers.notification.warning("Hay availability must be greater than 0");
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  // HANDLE ESTIMATE HAY
  const estimateHay = async () => {
    setIsSmartEstimating(true);
    const payload = {
      images: productForm.images,
    };
    try {
      const response = await ProductApi.EsimateHay(payload);
      if (response.status === 200) {
        const data = response.data.data;
        setIsSmartEstimating(false);
        setProductForm((prevForm) => ({
          ...prevForm,
          shape:
            data.Shape !== "Unknown"
              ? data.Shape == "Rectangular"
                ? "ssquare"
                : data.Shape.toLowerCase()
              : "",
          hay_compression:
            data.Compression !== "Unknown"
              ? data.Compression.toLowerCase()
              : "",
          hay_density:
            data.Density !== "Unknown" ? Number(data.Density).toFixed(7) : "",
          hay_type:
            data.HayType !== "Unknown"
              ? data.HayType === "Alfalfa"
                ? data.HayType
                : data.HayType === "Straw"
                ? "Sainfoin"
                : "Brome Grass"
              : "",
        }));
      }
    } catch (error) {
      Helpers.notification.error("Fail to fetch. Please try again");
      errorLog("ProductRegister.tsx - estimateHay", error);
    }
  };

  const reEstimate = async (): Promise<IProductForm> => {
    const newProductForm = structuredClone(productForm);

    // Convert TON => KG
    newProductForm.weight_per_bale = (
      parseFloat(productForm.weight_per_bale) * 1000
    ).toFixed(2);

    // Convert TON => KG
    newProductForm.total_weight = (
      parseFloat(productForm.total_weight) * 1000
    ).toFixed(2);

    // Convert CAD => USD
    newProductForm.price_per_tons = convertCurrency(
      productForm.price_per_tons,
      units.currency,
      "USD"
    );

    // Convert CAD => USD
    newProductForm.price_per_bale = convertCurrency(
      productForm.price_per_bale,
      units.currency,
      "USD"
    );

    // Convert CAD => USD
    newProductForm.current_price = convertCurrency(
      productForm.current_price,
      units.currency,
      "USD"
    );
    return newProductForm;
  };

  // HANDLE REGISTER PRODUCT
  const registerProduct = async () => {
    if (productForm.price_per_tons === 0 || productForm.current_price === 0) {
      Helpers.notification.warning("Please fill out the required fields");
      return;
    }
    if (Object.keys(productForm.shipping_options_details).length === 0) {
      Helpers.notification.warning("Please select supported shipping options");
      return;
    }
    if (productForm.payment_info_ids.length === 0) {
      Helpers.notification.warning("Please select at least one payment method");
      return;
    }
    const payload = await reEstimate();
    setIsRegistering(true);
    try {
      const response = await ProductApi.ProductRegistration(payload);
      if (
        response.status === 200 &&
        response.data.message === "Product created successfully"
      ) {
        setIsNewProductState(true);
        setIsRegistering(false);
        navigate("/products");
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("ProductRegister.tsx - registerProduct", error);
    }
  };

  const updateProduct = async () => {
    if (productForm.price_per_tons === 0 || productForm.current_price === 0) {
      Helpers.notification.warning("Please fill out the required fields");
      return;
    }
    if (Object.keys(productForm.shipping_options_details).length === 0) {
      Helpers.notification.warning(
        "Please select supported shipping options and payment method"
      );
      return;
    }
    if (productForm.payment_info_ids.length === 0) {
      Helpers.notification.warning("Please select at least one payment method");
      return;
    }
    const payload = await reEstimate();
    setIsRegistering(true);
    try {
      const response = await ProductApi.UpdateProduct(payload);
      if (
        response.status === 200 &&
        response.data.message === "Product updated successfully"
      ) {
        setIsNewProductState(true);
        setIsRegistering(false);
        Helpers.showSuccessUpdated("Product updated successfully");
        window.location.reload();
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("ProductRegister.tsx - updateProduct", error);
    }
  };

  // CALCULATE HAY DENSITY
  useEffect(() => {
    if (
      hayTypeClassify !== "" &&
      productForm.hay_compression !== "" &&
      productForm.shape !== ""
    ) {
      const result = HayCalculations.calculateDensity(
        hayTypeClassify,
        productForm.hay_compression,
        productForm.shape
      );
      setIsEstimate(true);
      setProductForm({
        ...productForm,
        hay_density: result.toFixed(7),
      });
    }
  }, [hayTypeClassify, productForm.shape, productForm.hay_compression]);

  // CALCULATE WEIGHT PER BALE
  useEffect(() => {
    if (productForm.shape === "") return;

    let sizeSelect = "cust.";
    let newDimensions = {};

    if (productForm.shape === "ssquare") {
      sizeSelect = "small";
      newDimensions = { width: 1.17, height: 1.5, length: 3 };
      setProductForm((prev) => ({ ...prev, size: "1.17 x 1.5 x 3" }));
    } else if (productForm.shape === "lsquare") {
      sizeSelect = "large";
      newDimensions = { width: 4, height: 4, length: 8 };
      setProductForm((prev) => ({ ...prev, size: "4 x 4 x 8" }));
    } else {
      newDimensions = { width: 5, diameter: 6 };
      setProductForm((prev) => ({ ...prev, size: "5 x 6" }));
    }

    setSizeSelect(sizeSelect);
    setDimensions(newDimensions);

    // If conditions full, calculate weight per bale
    if (
      hayTypeClassify !== "" &&
      productForm.hay_compression !== "" &&
      productForm.moisture !== ""
    ) {
      const hayDensityResult = HayCalculations.calculateDensity(
        hayTypeClassify,
        productForm.hay_compression,
        productForm.shape
      );
      const weightPerBaleResult = HayCalculations.estimateBaleWeight(
        hayTypeClassify,
        productForm.shape,
        newDimensions,
        productForm.hay_compression,
        productForm.moisture
      );
      setIsEstimate(true);
      setProductForm((prev: any) => ({
        ...prev,
        hay_density: hayDensityResult.toFixed(7),
        weight_per_bale: convertWeight(
          weightPerBaleResult,
          "kg",
          selectedWeightUnit.weightPerBale
        ).toFixed(selectedWeightUnit.weightPerBale === "t" ? 4 : 0),
      }));
    }
  }, [
    hayTypeClassify,
    productForm.shape,
    productForm.hay_compression,
    productForm.moisture,
  ]);

  // CALCULATE TOTAL WEIGHT
  useEffect(() => {
    if (
      productForm.hay_availability !== "" &&
      productForm.weight_per_bale !== "" &&
      !isTypingTotalWeight
    ) {
      const result =
        parseFloat(productForm.hay_availability) *
        convertWeight(
          productForm.weight_per_bale,
          selectedWeightUnit.weightPerBale,
          "t"
        );
      setProductForm({
        ...productForm,
        total_weight: convertWeight(
          result,
          "t",
          selectedWeightUnit.totalWeight
        ).toFixed(selectedWeightUnit.totalWeight === "t" ? 4 : 0),
      });
    }
  }, [productForm.weight_per_bale, productForm.hay_availability]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // HANDLE CHANGE UNIT
  const handleChangeUnit = (key: string, unit: string) => {
    if (key === "totalWeight") {
      setSelectedWeightUnit({
        ...selectedWeightUnit,
        [key]: unit,
      });

      const totalWeightConverted = convertWeight(
        productForm.total_weight,
        selectedWeightUnit.totalWeight,
        unit
      );

      const totalWeightInTon = convertWeight(
        productForm.total_weight,
        selectedWeightUnit.totalWeight,
        "t"
      );
      // unit: ton
      const newBaleWeight =
        totalWeightInTon / parseFloat(productForm.hay_availability);

      // unit: kg/ft3
      const newDensity = HayCalculations.reEstimateDensity(
        productForm.shape,
        dimensions,
        (newBaleWeight * 1000).toString()
      );

      setProductForm({
        ...productForm,
        hay_density: newDensity.toFixed(2),
        total_weight: totalWeightConverted.toFixed(unit === "t" ? 4 : 0),
        weight_per_bale: convertWeight(
          newBaleWeight,
          "t",
          selectedWeightUnit.weightPerBale
        ).toFixed(selectedWeightUnit.weightPerBale === "t" ? 4 : 0),
      });
    } else {
      setSelectedWeightUnit({
        ...selectedWeightUnit,
        [key]: unit,
      });

      const baleWeightConverted = convertWeight(
        productForm.weight_per_bale,
        selectedWeightUnit.weightPerBale,
        unit
      );

      const baleWeightInTon = convertWeight(
        productForm.weight_per_bale,
        selectedWeightUnit.weightPerBale,
        "t"
      );
      const newDensity = HayCalculations.reEstimateDensity(
        productForm.shape,
        dimensions,
        (parseFloat(baleWeightInTon) * 1000).toString()
      );

      const newTotalWeight =
        baleWeightInTon * parseFloat(productForm.hay_availability);

      setProductForm({
        ...productForm,
        weight_per_bale: baleWeightConverted.toFixed(unit === "t" ? 4 : 0),
        hay_density: newDensity.toFixed(2),
        total_weight: convertWeight(
          newTotalWeight,
          "t",
          selectedWeightUnit.totalWeight
        ).toFixed(selectedWeightUnit.totalWeight === "t" ? 4 : 0),
      });
    }
  };

  // HANDLE UNDO
  const handleUndoAll = () => {
    // kg/ft3
    const newDensity = HayCalculations.calculateDensity(
      hayTypeClassify,
      productForm.hay_compression,
      productForm.shape
    );

    // kg
    const newBaleWeight = HayCalculations.estimateBaleWeight(
      hayTypeClassify,
      productForm.shape,
      dimensions,
      productForm.hay_compression,
      productForm.moisture
    );

    const newTotalWeight =
      convertWeight(newBaleWeight, "kg", selectedWeightUnit.weightPerBale) *
      parseFloat(productForm.hay_availability);

    setIsEstimate(true);
    setProductForm({
      ...productForm,
      hay_density: newDensity.toFixed(7),
      weight_per_bale: convertWeight(
        newBaleWeight,
        "kg",
        selectedWeightUnit.weightPerBale
      ).toFixed(selectedWeightUnit.weightPerBale === "t" ? 4 : 0),
      total_weight: convertWeight(
        newTotalWeight,
        "t",
        selectedWeightUnit.totalWeight
      ).toFixed(selectedWeightUnit.totalWeight === "t" ? 4 : 0),
    });
  };

  return (
    <div className={`ProductRegister${context || ""}`}>
      <div className="container">
        <div className="section-title">
          {context === "Edit"
            ? "Edit Information"
            : context === "Repost"
            ? "Repost Product"
            : "Product Registration"}
        </div>
        <div className="step-group">
          <div className={`step ${currentStep === 1 ? "active" : "done"}`}>
            Step 1: Images
          </div>
          <div
            className={`step ${
              currentStep < 2 ? "" : currentStep === 2 ? "active" : "done"
            }`}
          >
            Step 2: Information
          </div>
          <div className={`step ${currentStep === 3 ? "active" : ""}`}>
            Step 3: Pricing
          </div>
        </div>
        <div className="step-wrap mt-5">
          {currentStep === 1 && (
            <ImageStep
              productForm={productForm}
              setProductForm={setProductForm}
              isSmartEstimate={isSmartEstimate}
              setIsSmartEstimate={setIsSmartEstimate}
            />
          )}
          {currentStep === 2 && (
            <InfoStep
              productForm={productForm}
              setProductForm={setProductForm}
              userProfile={userProfile!}
              isSmartEstimate={isSmartEstimate}
              isSmartEstimating={isSmartEstimating}
              dimensions={dimensions}
              hayTypeClassify={hayTypeClassify}
              setHayTypeClassify={setHayTypeClassify}
              isEstimate={isEstimate}
              setIsEstimate={setIsEstimate}
              isTypingTotalWeight={isTypingTotalWeight}
              setIsTypingTotalWeight={setIsTypingTotalWeight}
              sizeSelect={sizeSelect}
              selectedWeightUnit={selectedWeightUnit}
              handleChangeUnit={handleChangeUnit}
              handleUndoAll={handleUndoAll}
            />
          )}
          {currentStep === 3 && (
            <PricingStep
              productForm={productForm}
              setProductForm={setProductForm}
              metrics={metrics}
              selectedWeightUnit={selectedWeightUnit}
              handleChangeUnit={handleChangeUnit}
              isEstimate={isEstimate}
              setIsEstimate={setIsEstimate}
              handleUndoAll={handleUndoAll}
            />
          )}
        </div>
        <div className="form-submit">
          {currentStep > 1 && (
            <Button
              className="back-btn"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Back
            </Button>
          )}
          <Button className="save-btn">Save as Draft</Button>
          <Button
            className="continue-btn"
            onClick={
              currentStep === 3
                ? context === "Edit"
                  ? updateProduct
                  : registerProduct
                : handleNextStep
            }
            loading={isRegistering}
            iconPosition="start"
          >
            {currentStep < 3
              ? "Continue"
              : context === "Edit"
              ? "Save"
              : "Register"}
          </Button>
          <Modal
            title={
              <span>
                <ExclamationCircleOutlined
                  style={{ color: "#faad14", marginRight: 8 }}
                />
                Warning
              </span>
            }
            open={isTriggerModal}
            okText="Go to farm profile"
            okButtonProps={{ className: "btn-redirect" }}
            onCancel={() => {
              navigate(-1);
            }}
            onOk={() => {
              navigate("/profile?direct=farm");
            }}
          >
            <p>
              You need to complete your farm profile before register product
            </p>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default ProductRegister;
